import { useState, useMemo, memo } from 'react';
import { ProductCard } from './ProductCard';
import type { Product, WeightVariant } from './ProductCard';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useShopifyProducts } from '../hooks/useShopifyProducts';
import { useTranslation } from 'react-i18next';
import { useCurrentMarket } from '../hooks/useMarketZustand';
import { logger } from '../utils/logger';

// Mock data for products with weight variants and detailed descriptions
const mockProducts: Product[] = [
  {
    id: '1',
    name: "Chef's Signature Garam Masala",
    description:
      'A masterfully crafted blend of 12 aromatic spices including cardamom, cinnamon, and cloves. This signature blend delivers authentic warmth and depth to curries, rice dishes, and roasted vegetables.',
    price: 18.99,
    image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400',
    rating: 5,
    reviewCount: 124,
    tags: ['Signature', 'Spicy', 'Indian'],
    isBestseller: true,
    isCertifiedOrganic: true,
    origin: 'Kerala, India',
    weightVariants: [
      { weight: '50', unit: 'g', price: 18.99, pricePerKg: 379.8 },
      { weight: '100', unit: 'g', price: 32.99, pricePerKg: 329.9 },
      { weight: '250', unit: 'g', price: 74.99, pricePerKg: 299.96 },
    ],
  },
  {
    id: '2',
    name: 'Organic Saffron Threads',
    description:
      'Hand-harvested Kashmiri saffron threads with exceptional aroma and deep golden color. Each thread is carefully selected for premium quality, delivering unparalleled flavor to rice, desserts, and teas.',
    price: 89.99,
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400',
    rating: 5,
    reviewCount: 89,
    tags: ['Organic', 'Premium', 'Saffron'],
    isCertifiedOrganic: true,
    origin: 'Kashmir, India',
    weightVariants: [
      { weight: '1', unit: 'g', price: 89.99, pricePerKg: 89990.0 },
      { weight: '2', unit: 'g', price: 169.99, pricePerKg: 84995.0 },
      { weight: '5', unit: 'g', price: 399.99, pricePerKg: 79998.0 },
    ],
  },
  {
    id: '3',
    name: 'Mediterranean Herb Blend',
    description:
      'Sun-dried oregano, thyme, and rosemary from the Mediterranean coast. This aromatic blend captures the essence of coastal cuisine, perfect for pasta, grilled vegetables, and olive oil infusions.',
    price: 14.99,
    image: 'https://images.unsplash.com/photo-1559181567-c3190ca9959b?w=400',
    rating: 4,
    reviewCount: 67,
    tags: ['Herbs', 'Mediterranean', 'Organic'],
    isNew: true,
    isCertifiedOrganic: true,
    origin: 'Greece',
    weightVariants: [
      { weight: '75', unit: 'g', price: 14.99, pricePerKg: 199.87 },
      { weight: '150', unit: 'g', price: 26.99, pricePerKg: 179.93 },
      { weight: '300', unit: 'g', price: 49.99, pricePerKg: 166.63 },
    ],
  },
  {
    id: '4',
    name: 'Smoky BBQ Rub',
    description:
      'Bold blend of smoked paprika, brown sugar, and cumin with a hint of chipotle. Creates the perfect caramelized crust on meats while infusing deep, smoky flavors throughout.',
    price: 16.99,
    image: 'https://images.unsplash.com/photo-1583049112947-84fbb2dd9ad4?w=400',
    rating: 5,
    reviewCount: 156,
    tags: ['BBQ', 'Smoky', 'American'],
    isBestseller: true,
    isCertifiedOrganic: false,
    origin: 'Texas, USA',
    weightVariants: [
      { weight: '100', unit: 'g', price: 16.99, pricePerKg: 169.9 },
      { weight: '200', unit: 'g', price: 29.99, pricePerKg: 149.95 },
      { weight: '500', unit: 'g', price: 69.99, pricePerKg: 139.98 },
    ],
  },
  {
    id: '5',
    name: 'Himalayan Pink Salt',
    description:
      'Pure, unrefined pink salt crystals from ancient Himalayan deposits. Rich in natural minerals with a clean, mild flavor that enhances without overpowering your favorite dishes.',
    price: 12.99,
    image: 'https://images.unsplash.com/photo-1576672843344-977b7b7c04e7?w=400',
    rating: 4,
    reviewCount: 203,
    tags: ['Salt', 'Himalayan', 'Natural'],
    isCertifiedOrganic: false,
    origin: 'Pakistan',
    weightVariants: [
      { weight: '250', unit: 'g', price: 12.99, pricePerKg: 51.96 },
      { weight: '500', unit: 'g', price: 21.99, pricePerKg: 43.98 },
      { weight: '1', unit: 'kg', price: 39.99, pricePerKg: 39.99 },
    ],
  },
  {
    id: '6',
    name: 'Asian Five-Spice Blend',
    description:
      'Traditional Chinese blend of star anise, cloves, fennel, cinnamon, and Szechuan peppercorns. Balances sweet, sour, bitter, pungent, and salty flavors in perfect harmony.',
    price: 13.99,
    image: 'https://images.unsplash.com/photo-1596545422411-d9a1b30cf96a?w=400',
    rating: 4,
    reviewCount: 91,
    tags: ['Asian', 'Traditional', 'Blend'],
    isNew: true,
    isCertifiedOrganic: true,
    origin: 'China',
    weightVariants: [
      { weight: '60', unit: 'g', price: 13.99, pricePerKg: 233.17 },
      { weight: '120', unit: 'g', price: 24.99, pricePerKg: 208.25 },
      { weight: '250', unit: 'g', price: 47.99, pricePerKg: 191.96 },
    ],
  },
];

interface ProductSectionsProps {
  onAddToCart?: (product: Product, variant?: WeightVariant) => void;
  onProductClick?: (product: Product) => void;
  onViewAllClick?: () => void;
}

function ProductSectionsComponent({
  onAddToCart,
  onProductClick,
  onViewAllClick,
}: ProductSectionsProps) {
  const { t } = useTranslation(['homepage', 'common']);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const currentMarket = useCurrentMarket();

  // Load real Shopify products (back to original hook temporarily)
  const { products: shopifyProducts, loading, error } = useShopifyProducts(6);

  const handleToggleFavorite = useMemo(
    () => (productId: string) => {
      setFavorites(prev => {
        const newFavorites = new Set(prev);
        if (newFavorites.has(productId)) {
          newFavorites.delete(productId);
        } else {
          newFavorites.add(productId);
        }
        return newFavorites;
      });
    },
    []
  );

  // Memoize expensive calculations to prevent re-renders
  const products = useMemo(() => {
    return shopifyProducts.length > 0 ? shopifyProducts : mockProducts;
  }, [shopifyProducts]);

  const bestsellers = useMemo(() => {
    return products.slice(0, 3); // First 3 as bestsellers
  }, [products]);

  const newArrivals = useMemo(() => {
    return products.slice(3); // Rest as new arrivals
  }, [products]);

  const ProductGrid = ({
    products,
    title,
  }: {
    products: Product[];
    title: string;
  }) => (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{title}</h2>
            <p className="text-gray-600 text-lg max-w-2xl">
              {title === t('sections.bestsellers.title')
                ? t('sections.bestsellers.subtitle')
                : t('sections.newArrivals.subtitle')}
            </p>
          </div>
          <Button
            variant="outline"
            className="border-amber-900 text-amber-900 hover:bg-amber-50 px-8 py-3 font-semibold"
            onClick={onViewAllClick}
            style={{ borderRadius: '12px' }}
          >
            <span className="hidden sm:inline">
              {t('common:navigation.viewAll')}
            </span>
            <span className="sm:hidden">
              {t('common:navigation.viewAllShort')}
            </span>
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map(product => (
            <ProductCard
              key={`${product.id}-${currentMarket}`}
              product={product}
              onAddToCart={onAddToCart}
              onToggleFavorite={handleToggleFavorite}
              isFavorite={favorites.has(product.id)}
              onClick={() => onProductClick?.(product)}
            />
          ))}
        </div>
      </div>
    </section>
  );

  // Show loading state
  if (loading && shopifyProducts.length === 0) {
    return (
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-pulse">Loading products...</div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state with fallback to mock data
  if (error && shopifyProducts.length === 0) {
    logger.warn('Failed to load Shopify products, using mock data:', error);
  }

  return (
    <div
      className="relative"
      style={{
        background:
          'linear-gradient(135deg, rgba(249, 250, 251, 0.8) 0%, rgba(255, 255, 255, 0.9) 100%)',
      }}
    >
      {/* Subtle background pattern for 2025 aesthetic */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(245, 158, 11, 0.15) 1px, transparent 0)',
          backgroundSize: '24px 24px',
        }}
      />

      <div className="relative">
        <ProductGrid
          products={bestsellers}
          title={t('sections.bestsellers.title')}
        />
        <ProductGrid
          products={newArrivals}
          title={t('sections.newArrivals.title')}
        />
      </div>
    </div>
  );
}

// Wrap with memo to prevent unnecessary re-renders
export const ProductSections = memo(ProductSectionsComponent);
